import React, { useState, useEffect, useRef, useMemo } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Row,
  Col,
  Form,
  Modal,
  Alert,
  Spinner,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  getFirestore,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaPlus,
  FaTrash,
  FaEye,
  FaSearch,
  FaSync,
  FaUserGraduate,
  FaBook,
  FaCertificate,
  FaDownload,
  FaUpload,
  FaFileExcel,
  FaChartPie,
  FaClipboardList,
  FaUserCheck,
  FaHourglassHalf,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { generarCertificadoPDF } from "./utils/GenerarCertificado";
import firebaseApp from "../../firebase/credenciales";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

const UsuariosPage = ({ usuario }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [cursos, setCursos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [progresos, setProgresos] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroCurso, setFiltroCurso] = useState("TODOS");
  const [filtroEstado, setFiltroEstado] = useState("TODOS");
  const [paginaActual, setPaginaActual] = useState(1);
  const filasPorPagina = 25;
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [cursoSeleccionado, setCursoSeleccionado] = useState("");
  const [usuariosSeleccionados, setUsuariosSeleccionados] = useState([]);
  const [searchModal, setSearchModal] = useState(""); 
  const [modalLimit, setModalLimit] = useState(100);
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (usuario?.ruc) {
      cargarDatos();
    }
  }, [usuario]);

  useEffect(() => {
    setPaginaActual(1);
  }, [searchTerm, filtroCurso, filtroEstado, asignaciones.length]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";
      const cursosRef = collection(db, `in_capacitacion/${rucEmpresa}/programas`);
      const usuariosRef = query(collection(db, "users"), where("rucEmpresa", "==", rucEmpresa));
      const asignacionesRef = collection(db, `in_capacitacion/${rucEmpresa}/asignaciones`);
      const progresosRef = collection(db, `in_capacitacion/${rucEmpresa}/progresos`);

      const [cursosSnapshot, usuariosSnapshot, asignacionesSnapshot, progresosSnapshot] = await Promise.all([
        getDocs(cursosRef),
        getDocs(usuariosRef),
        getDocs(asignacionesRef),
        getDocs(progresosRef),
      ]);

      const cursosData = cursosSnapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          nombre: data.nombre || "Sin nombre",
          descripcion: data.descripcion || "",
          modulos: data.modulos || [],
        };
      });

      const usuariosData = usuariosSnapshot.docs.map((d) => {
        const data = d.data();
        return {
          dni: d.id,
          nombre: data.name || "Sin nombre",
          apellido: data.apellidos || "",
          email: data.email || "",
          cargo: data.cargo || "",
        };
      });

      const asignacionesData = asignacionesSnapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          cursoId: data.cursoId || "",
          dni: data.dni || "",
          fechaAsignacion: data.fechaAsignacion || null,
        };
      });

      const progresosData = {};
      progresosSnapshot.forEach((d) => {
        const data = d.data();
        progresosData[d.id] = {
          modulosCompletados: Array.isArray(data.modulosCompletados)
            ? data.modulosCompletados
            : [],
        };
      });

      setCursos(cursosData);
      setUsuarios(usuariosData);
      setAsignaciones(asignacionesData);
      setProgresos(progresosData);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      setError("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const cursosById = useMemo(() => {
    const m = new Map();
    cursos.forEach((c) => m.set(c.id, c));
    return m;
  }, [cursos]);

  const usuariosByDni = useMemo(() => {
    const m = new Map();
    usuarios.forEach((u) => m.set(u.dni, u));
    return m;
  }, [usuarios]);

  const asignacionesSet = useMemo(() => {
    return new Set(asignaciones.map((a) => `${a.cursoId}__${a.dni}`));
  }, [asignaciones]);

  const calcularProgreso = (dni, cursoId) => {
    const curso = cursosById.get(cursoId);
    if (!curso || !progresos[dni]) return { completados: 0, total: 0, porcentaje: 0 };

    const totalModulos = curso.modulos.length;
    const modulosCompletados = progresos[dni].modulosCompletados.filter((m) =>
      m.startsWith(`${cursoId}_`)
    ).length;

    return {
      completados: modulosCompletados,
      total: totalModulos,
      porcentaje: totalModulos > 0 ? (modulosCompletados / totalModulos) * 100 : 0,
    };
  };

  const obtenerEstado = (dni, cursoId) => {
    const progreso = calcularProgreso(dni, cursoId);
    if (progreso.completados === progreso.total && progreso.total > 0) {
      return "COMPLETADO";
    } else if (progreso.completados > 0) {
      return "EN PROGRESO";
    } else {
      return "INSCRITO";
    }
  };

  const descargarPlantillaExcel = () => {
    try {
      const cursoEjemplo = cursos[0] || { id: "ID_DEL_CURSO" };
      const usuarioEjemplo = usuarios[0] || { dni: "77777777" };

      const worksheetData = [
        ["ID de Curso", "DNI"],
        [cursoEjemplo.id, usuarioEjemplo.dni],
      ];

      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Plantilla_Asignacion");
      const wscols = [{ wch: 25 }, { wch: 15 }];
      ws["!cols"] = wscols;
      XLSX.writeFile(wb, "Plantilla_Carga_Masiva_Capacitaciones.xlsx");
    } catch (error) {
      console.error("Error al generar plantilla:", error);
      setError("Error al generar la plantilla");
    }
  };

  const manejarImportacionExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      setError("");
      setSuccess("");
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);

        if (rows.length === 0) {
          setError("El archivo está vacío");
          setImporting(false);
          return;
        }

        let procesados = 0;
        let errores = 0;
        const rucEmpresa = usuario?.ruc || "";

        for (const row of rows) {
          const cursoId = row["ID de Curso"]?.toString().trim();
          const dni = row["DNI"]?.toString().trim();

          if (cursoId && dni) {
            const cursoExiste = cursos.some(c => c.id === cursoId);
            if (!cursoExiste) { errores++; continue; }

            const asignacionId = `${cursoId}_${dni}`;
            const asignacionRef = doc(db, `in_capacitacion/${rucEmpresa}/asignaciones`, asignacionId);
            
            await setDoc(asignacionRef, {
              cursoId,
              dni,
              fechaAsignacion: new Date(),
            });
            procesados++;
          } else { errores++; }
        }

        setSuccess(`Carga masiva completada: ${procesados} procesados correctamente, ${errores} errores.`);
        
        // AUDITORÍA MASIVA
        try {
          await logBusinessAction("ASIGNACION_MASIVA_CURSOS", "GestionCapacitaciones", {
            procesados,
            errores,
            cambiosRealizados: { cargaMasiva: { anterior: null, nuevo: `Se procesaron ${procesados} asignaciones` } }
          }, usuario);
        } catch (e) {
          console.warn("No se pudo auditar la carga masiva", e);
        }

        if (fileInputRef.current) fileInputRef.current.value = "";
        setImporting(false);
        cargarDatos();
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error al procesar excel:", error);
      setError("Error al procesar el archivo Excel.");
      setImporting(false);
    }
  };

  const prepararDatosTabla = () => {
    const datos = [];
    asignaciones.forEach((asignacion) => {
      const usuarioData = usuariosByDni.get(asignacion.dni);
      const cursoData = cursosById.get(asignacion.cursoId);
      if (usuarioData && cursoData) {
        datos.push({
          ...asignacion,
          nombreUsuario: `${usuarioData.nombre} ${usuarioData.apellido}`,
          emailUsuario: usuarioData.email,
          cargoUsuario: usuarioData.cargo,
          nombreCurso: cursoData.nombre,
          progreso: calcularProgreso(asignacion.dni, asignacion.cursoId),
          estado: obtenerEstado(asignacion.dni, asignacion.cursoId),
        });
      }
    });
    return datos;
  };

  const datosCompletos = prepararDatosTabla();
  const stats = {
    totalAsignaciones: asignaciones.length,
    usuariosActivos: new Set(asignaciones.map(a => a.dni)).size,
    certificados: datosCompletos.filter(d => d.estado === "COMPLETADO").length,
    enDesarrollo: datosCompletos.filter(d => d.estado !== "COMPLETADO").length,
  };

  const datosFiltrados = datosCompletos.filter((dato) => {
    const cumpleBusqueda = dato.nombreUsuario.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          dato.emailUsuario.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          dato.dni.toLowerCase().includes(searchTerm.toLowerCase());
    const cumpleCurso = filtroCurso === "TODOS" || dato.cursoId === filtroCurso;
    const cumpleEstado = filtroEstado === "TODOS" || dato.estado === filtroEstado;
    return cumpleBusqueda && cumpleCurso && cumpleEstado;
  });

  const totalPaginas = Math.max(1, Math.ceil(datosFiltrados.length / filasPorPagina));
  const paginaSegura = Math.min(paginaActual, totalPaginas);
  const inicio = (paginaSegura - 1) * filasPorPagina;
  const fin = inicio + filasPorPagina;
  const datosPagina = datosFiltrados.slice(inicio, fin);

  const abrirModalAsignar = () => {
    setShowModal(true);
    setCursoSeleccionado("");
    setUsuariosSeleccionados([]);
    setSearchModal("");
    setModalLimit(100);
    setError("");
    setSuccess("");
  };

  const obtenerUsuariosDisponibles = () => {
    const disponibles = usuarios.filter(
      (u) => !asignacionesSet.has(`${cursoSeleccionado}__${u.dni}`)
    );
    const filtrados = disponibles.filter(u => {
      const searchLower = searchModal.toLowerCase();
      return u.dni.toLowerCase().includes(searchLower) || u.apellido.toLowerCase().includes(searchLower) || u.nombre.toLowerCase().includes(searchLower);
    });
    return filtrados.sort((a, b) => a.apellido.localeCompare(b.apellido));
  };

  const usuariosDisponibles = useMemo(() => obtenerUsuariosDisponibles(), [usuarios, asignacionesSet, cursoSeleccionado, searchModal]);
  const usuariosDisponiblesRender = usuariosDisponibles.slice(0, modalLimit);

  const toggleTodosUsuarios = (checked) => {
    if (checked) setUsuariosSeleccionados(usuariosDisponibles.map(u => u.dni));
    else setUsuariosSeleccionados([]);
  };

  const toggleUsuario = (dni) => {
    if (usuariosSeleccionados.includes(dni)) setUsuariosSeleccionados(usuariosSeleccionados.filter(d => d !== dni));
    else setUsuariosSeleccionados([...usuariosSeleccionados, dni]);
  };

  const asignarUsuarios = async () => {
    if (!cursoSeleccionado || usuariosSeleccionados.length === 0) {
      setError("Debes seleccionar un curso y al menos un usuario");
      return;
    }
    try {
      setLoadingModal(true);
      const rucEmpresa = usuario?.ruc || "";
      for (const dni of usuariosSeleccionados) {
        const asignacionId = `${cursoSeleccionado}_${dni}`;
        const asignacionRef = doc(db, `in_capacitacion/${rucEmpresa}/asignaciones`, asignacionId);
        await setDoc(asignacionRef, { cursoId: cursoSeleccionado, dni, fechaAsignacion: new Date() });
      }

      // AUDITORÍA MANUAL
      const cursoData = cursos.find(c => c.id === cursoSeleccionado);
      try {
        await logBusinessAction("ASIGNACION_CURSO", "GestionCapacitaciones", {
          curso: cursoData?.nombre || cursoSeleccionado,
          usuariosAsignados: usuariosSeleccionados.length,
          cambiosRealizados: {
            asignacion: { anterior: "No asignado", nuevo: "Asignado" }
          }
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de asignación", e);
      }

      setSuccess(`${usuariosSeleccionados.length} usuario(s) asignado(s) exitosamente`);
      setTimeout(() => { setShowModal(false); cargarDatos(); }, 1500);
    } catch (error) {
      console.error("Error al asignar usuarios:", error);
      setError("Error al asignar usuarios.");
    } finally { setLoadingModal(false); }
  };

  const desasignarUsuario = async (asignacionId, nombreUsuario, nombreCurso) => {
    if (!window.confirm(`¿Desasignar a ${nombreUsuario} del curso "${nombreCurso}"?`)) return;
    try {
      const rucEmpresa = usuario?.ruc || "";
      await deleteDoc(doc(db, `in_capacitacion/${rucEmpresa}/asignaciones`, asignacionId));

      // AUDITORÍA DESASIGNACION
      try {
        await logBusinessAction("DESASIGNACION_CURSO", "GestionCapacitaciones", {
          usuarioAfectado: nombreUsuario,
          curso: nombreCurso,
          cambiosRealizados: {
            asignacion: { anterior: "Asignado", nuevo: "No asignado" }
          }
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar auditoría de desasignación", e);
      }

      setSuccess("Usuario desasignado exitosamente");
      setTimeout(() => setSuccess(""), 3000);
      cargarDatos();
    } catch (error) { setError("Error al desasignar usuario"); }
  };

  const handleDescargarCertificado = async (dato) => {
    try {
      await generarCertificadoPDF({
        nombreUsuario: dato.nombreUsuario,
        dni: dato.dni,
        nombreCurso: dato.nombreCurso,
        fecha: new Date().toLocaleDateString(),
        empresa: usuario?.nameEmpresa || "Mi Boleta"
      });
    } catch (error) { alert("Error al generar certificado"); }
  };

  const getBadgeEstado = (estado) => {
    switch (estado) {
      case "COMPLETADO": return "success";
      case "EN PROGRESO": return "warning";
      case "INSCRITO": return "info";
      default: return "secondary";
    }
  };

  return (
    <div className="min-vh-100" style={{ background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)", padding: "20px 0" }}>
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        {loading && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75" style={{ zIndex: 1080 }}>
            <div className="text-center">
              <Spinner animation="border" variant="light" style={{ width: "3rem", height: "3rem" }} />
              <p className="text-white mt-3">Sincronizando asignaciones...</p>
            </div>
          </div>
        )}

        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          <Card.Header className="text-white py-4 position-relative" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1"><FaUsers className="me-3" />Asignaci&oacute;n de Usuarios a Cursos</h2>
              <p className="mb-0 opacity-75">Gestiona el v&iacute;nculo masivo de colaboradores con los programas de capacitaci&oacute;n</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}><div className="rounded-3 p-3 text-center" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}><FaUserGraduate size={24} /></div></Col>
                  <Col md={5}>
                    <h5 className="mb-1 fw-semibold">Panel de Control de Asignaciones</h5>
                    <p className="text-muted mb-0 small">Altas y bajas en el cat&aacute;logo de capacitaciones</p>
                  </Col>
                  <Col md={6} className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      <Button variant="outline-primary" onClick={descargarPlantillaExcel} className="d-flex align-items-center gap-2" style={{ borderRadius: "10px" }}><FaDownload /> Plantilla</Button>
                      <Button variant="outline-success" onClick={() => fileInputRef.current.click()} className="d-flex align-items-center gap-2" style={{ borderRadius: "10px" }} disabled={importing}><FaUpload /> {importing ? "Cargando..." : "Carga Masiva (Excel)"}</Button>
                      <input type="file" ref={fileInputRef} hidden accept=".xlsx, .xls" onChange={manejarImportacionExcel} />
                      <Button className="d-flex align-items-center gap-2 text-white" onClick={abrirModalAsignar} style={{ borderRadius: "10px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none" }}><FaPlus /> Asignar</Button>
                      <Button variant="outline-secondary" onClick={cargarDatos} style={{ borderRadius: "10px" }}><FaSync /></Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Master KPIs Section - MOVED INSIDE */}
            <Row className="mb-4 g-3">
              {[
                { label: "Asignaciones Totales", val: stats.totalAsignaciones, col: "#667eea", icon: FaClipboardList },
                { label: "Usuarios Participantes", val: stats.usuariosActivos, col: "#28a745", icon: FaUserCheck },
                { label: "Certificaciones Listas", val: stats.certificados, col: "#17a2b8", icon: FaCertificate },
                { label: "En Desarrollo", val: stats.enDesarrollo, col: "#ffc107", icon: FaHourglassHalf },
              ].map((kpi, i) => (
                <Col md={3} key={i}>
                  <Card className="shadow-sm border-0 h-100" style={{ borderRadius: "15px", backgroundColor: "#ffffff" }}>
                    <Card.Body className="d-flex align-items-center p-3">
                       <div className="rounded-circle p-3 me-3" style={{ backgroundColor: `${kpi.col}15`, color: kpi.col }}>
                          <kpi.icon size={22} />
                       </div>
                       <div>
                          <h4 className="fw-bold mb-0">{kpi.val}</h4>
                          <div className="small text-muted fw-bold text-uppercase" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>{kpi.label}</div>
                       </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="g-2">
                  <Col md={6}><Form.Control type="text" placeholder="Buscar por nombre, email o DNI..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ borderRadius: "10px" }} /></Col>
                  <Col md={3}><Form.Select value={filtroCurso} onChange={(e) => setFiltroCurso(e.target.value)} style={{ borderRadius: "10px" }}><option value="TODOS">Todos los cursos</option>{cursos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}</Form.Select></Col>
                  <Col md={3}><Form.Select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={{ borderRadius: "10px" }}><option value="TODOS">Todos los estados</option><option value="INSCRITO">Inscrito</option><option value="EN PROGRESO">En Progreso</option><option value="COMPLETADO">Completado</option></Form.Select></Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="table-responsive">
              <Table hover className="align-middle shadow-sm bg-white" style={{ borderRadius: "15px", overflow: "hidden" }}>
                <thead className="bg-light text-secondary small text-uppercase fw-bold">
                  <tr>
                    <th className="ps-4">Usuario</th>
                    <th>Curso</th>
                    <th className="text-center">Progreso</th>
                    <th className="text-center">Estado</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {datosFiltrados.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-4 text-muted">No se encontraron asignaciones que coincidan con los filtros</td></tr>
                  ) : datosPagina.map((dato) => (
                    <tr key={dato.id}>
                      <td className="ps-4 py-3">
                        <div className="fw-bold">{dato.nombreUsuario}</div>
                        <small className="text-muted">{dato.dni} - {dato.emailUsuario}</small>
                      </td>
                      <td>{dato.nombreCurso}</td>
                      <td style={{ width: "20%" }}>
                        <div className="px-3">
                          <ProgressBar now={dato.progreso.porcentaje} variant={dato.progreso.porcentaje === 100 ? "success" : "warning"} style={{ height: "6px" }} />
                          <small className="text-muted d-block text-center mt-1">{dato.progreso.porcentaje.toFixed(0)}% ({dato.progreso.completados}/{dato.progreso.total})</small>
                        </div>
                      </td>
                      <td className="text-center"><Badge bg={getBadgeEstado(dato.estado)}>{dato.estado}</Badge></td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1">
                          <Button variant="link" size="sm" onClick={() => navigate(`/capacitaciones/curso/${dato.cursoId}`)}><FaEye /></Button>
                          {dato.estado === "COMPLETADO" && <Button variant="link" size="sm" className="text-info" onClick={() => handleDescargarCertificado(dato)}><FaCertificate /></Button>}
                          <Button variant="link" size="sm" className="text-danger" onClick={() => desasignarUsuario(dato.id, dato.nombreUsuario, dato.nombreCurso)}><FaTrash /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {datosFiltrados.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                <small className="text-muted">
                  Mostrando {inicio + 1}-{Math.min(fin, datosFiltrados.length)} de {datosFiltrados.length}
                </small>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={paginaSegura <= 1}
                    onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </Button>
                  <small className="align-self-center text-muted">
                    Página {paginaSegura} / {totalPaginas}
                  </small>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={paginaSegura >= totalPaginas}
                    onClick={() => setPaginaActual((p) => Math.min(totalPaginas, p + 1))}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
            <Modal.Title><FaPlus className="me-2" /> Asignar Usuarios</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Seleccionar Curso *</Form.Label>
                <Form.Select value={cursoSeleccionado} onChange={(e) => setCursoSeleccionado(e.target.value)}>
                  <option value="">-- Selecciona --</option>
                  {cursos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </Form.Select>
              </Form.Group>
              {cursoSeleccionado && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Control type="text" placeholder="Buscar por DNI o Nombre..." value={searchModal} onChange={(e) => setSearchModal(e.target.value)} />
                  </Form.Group>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="fw-bold">Usuarios Disponibles</span>
                    <Form.Check type="checkbox" label="Seleccionar todos" checked={usuariosSeleccionados.length === usuariosDisponibles.length && usuariosSeleccionados.length > 0} onChange={(e) => toggleTodosUsuarios(e.target.checked)} />
                  </div>
                  <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "8px", padding: "10px" }}>
                    {usuariosDisponiblesRender.map(u => (
                      <Form.Check key={u.dni} type="checkbox" id={`u-${u.dni}`} label={`${u.apellido}, ${u.nombre} (${u.dni})`} checked={usuariosSeleccionados.includes(u.dni)} onChange={() => toggleUsuario(u.dni)} className="mb-2" />
                    ))}
                    {usuariosDisponibles.length > usuariosDisponiblesRender.length && (
                      <div className="d-flex justify-content-center mt-2">
                        <Button size="sm" variant="outline-primary" onClick={() => setModalLimit((v) => v + 100)}>
                          Cargar 100 más
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cerrar</Button>
            <Button disabled={loadingModal} onClick={asignarUsuarios} style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none", color: "white" }}>
              {loadingModal ? "Asignando..." : "Asignar Usuarios"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default UsuariosPage;
